import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styles from '../registration.module.scss'
import { convertResidenceLongFormToAlpha3, getOwnProfileUrl, getSignInUrl, isOfAge } from 'utils'
import { Formik, Form } from 'formik'
import BtnPrimary from 'components/generic/BtnPrimary'
import DateSelectGroup from 'components/generic/DateSelectGroup'
import InputWTN from 'components/generic/InputWTN'
import { useCountries } from 'hooks/use-countries'
import TermsAndPolicy, { TermsAndPolicyType } from '../common/TermsAndPolicy'
import cx from 'classnames'
import SearchFieldWTN, { Option } from 'components/generic/SearchFieldWTN'
import { addWalkUpPerson, getNationalAssociationOptions } from 'actions/walkUpReg-actions'
import { Provider, WalkUpPersonDetailsInput } from 'types/global'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import UserMessageScreen from 'components/generic/UserMessageScreen'
import { MESSAGE_SCREENS, MessageTypes } from 'constants/message-screens'
import LoadingSpinner from '../common/LoadingSpinner'
import DisclaimerRecaptcha from '../common/DisclaimerRecaptcha'
import { useRecaptcha, withRecaptcha } from 'components/generic/hoc/withRecaptcha'
import ScrollToFormError from 'components/generic/ScrollToFormError'
import FormCheckbox from 'components/generic/FormCheckbox'
import FormInputGroup from 'components/generic/FormInputGroup'
import SexInputGroup from 'components/generic/SexInputGroup'
import { makeValidationFunc } from 'forms/validation/validators'
import { useWalkUpRegValidationSchema } from 'forms/validation/schemas'
import { PLACEHOLDERS, useFormLabels } from 'forms/common/translations'
import { FormikNames } from 'types/forms/formik-names'
import WelcomeExistingItfCustomer from './WelcomeExistingItfCustomer'
import { SexEnum, SignUpTypeEnum, WhereDidYouHearOptionEnum } from 'types/api/enums'
import Storage from 'modules/storage'
import ErrorScreen, { ErrorScreenProps } from 'components/generic/ErrorScreen'
import { startRegistrationFlow } from '../utils'
import ParentalConsent from '../common/ParentalConsent'
import { Persona } from 'forms/common/options'
import { useTranslation } from 'react-i18next'
import PersonaSelection from '../common/PersonaSelection'
import WhereDidYouHearSelection from '../common/WhereDidYouHearSelection'
import registrationApi from 'api/rest/registration'

const StartRegistrationFlow = ({ token }: { token: string }) => {
  useEffect(() => {
    startRegistrationFlow(token)
  }, [token])
  return <LoadingSpinner />
}

const initialFormValues = {
  [FormikNames.NATIVE_GIVEN_NAME]: '',
  [FormikNames.NATIVE_FAMILY_NAME]: '',
  [FormikNames.BIRTH_DATE]: '',
  [FormikNames.DOB_DAY]: '',
  [FormikNames.DOB_MONTH]: '',
  [FormikNames.DOB_YEAR]: '',
  [FormikNames.SEX]: '',
  [FormikNames.NATIONALITY_CODE]: '',
  [FormikNames.COUNTRY_OF_RESIDENCE_CODE]: '',
  [FormikNames.EMAIL_ADDRESS]: '',
  [FormikNames.COMM_CONSENT_ITF]: false,
  [FormikNames.COMM_CONSENT_NA]: false,
  [FormikNames.PARENT_GUARDIAN_NAME]: '',
  [FormikNames.PARENT_GUARDIAN_EMAIL]: '',
  [FormikNames.PERSONA]: '',
  [FormikNames.PERSONA_DETAIL]: '',
  [FormikNames.WHERE_DID_YOU_HEAR_ABOUT_US]: undefined,
  [FormikNames.WHERE_DID_YOU_HEAR_DETAIL]: '',
  [FormikNames.WHERE_DID_YOU_HEAR_NA]: '',
}

function formatNationalAssociationOptions(options: Provider[] | null | undefined) {
  if (!options) return []
  return options
    .map((option: Provider) => ({
      value: option.name,
      label: option.displayName || option.name,
    }))
    .sort((a: Option, b: Option) => a.label.localeCompare(b.label))
}

const WelcomeUser = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['strCommonUI', 'strRegister'])
  const labels = useFormLabels()
  const registrationToken = useSelector((state: AppState) => state.walkUpReg.registrationToken)
  const alreadyItfCustomer = useSelector((state: AppState) => state.walkUpReg.alreadyItfCustomer)
  const alreadyRegisteredForService = useSelector(
    (state: AppState) => state.walkUpReg.alreadyRegisteredForService
  )
  const isAddingPerson = useSelector((state: AppState) => state.walkUpReg.isAddingPerson)
  const addingPersonErrorType = useSelector((state: AppState) => state.walkUpReg.errorMsgType)
  const addingPersonError = useSelector((state: AppState) => state.walkUpReg.errorAddingPerson)
  const { residenceOptions, nationalityOptions } = useCountries()
  const [isResidenceValid, setIsResidenceValid] = useState(false)
  const [isValidatingResidence, setIsValidatingResidence] = useState(false)

  const loadingProviders = useSelector((state: AppState) => state.walkUpReg.loadingProviders)
  const nationalAssociationOptions = useSelector((state: AppState) => state.walkUpReg.providers)

  useEffect(() => {
    dispatch(getNationalAssociationOptions())
  }, [dispatch])

  const {
    isRecaptchaReady,
    isRequestingToken,
    error: errorRequestingRecaptchaToken,
    getRecaptchaToken,
  } = useRecaptcha()
  const displayError: ErrorScreenProps | null = useMemo(() => {
    const errorAdding = addingPersonError && {
      type: addingPersonErrorType,
      message: addingPersonError.message,
    }
    const errorRequestingRecaptcha = errorRequestingRecaptchaToken && {
      type: MessageTypes.DEFAULT,
      message: errorRequestingRecaptchaToken.message,
    }
    return errorAdding ?? errorRequestingRecaptcha
  }, [addingPersonError, errorRequestingRecaptchaToken, addingPersonErrorType])
  const walkUpRegValidationSchema = useWalkUpRegValidationSchema()
  const handleValidate = useCallback(
    values => {
      const allFields = Object.keys(initialFormValues)
      const parentalConsentFields = [
        FormikNames.PARENT_GUARDIAN_NAME,
        FormikNames.PARENT_GUARDIAN_EMAIL,
      ]
      const omitValidationFields: string[] = []

      if (!values.birthDate || isOfAge(values.birthDate))
        parentalConsentFields.forEach(f => omitValidationFields.push(f))

      if (values[FormikNames.PERSONA] !== Persona.OTHER)
        omitValidationFields.push(FormikNames.PERSONA_DETAIL)

      const fieldsToValidate = [...allFields].filter(f => !omitValidationFields.includes(f))
      const validate = makeValidationFunc(fieldsToValidate, walkUpRegValidationSchema)
      return validate(values)
    },
    [walkUpRegValidationSchema]
  )

  if (alreadyRegisteredForService)
    return (
      <UserMessageScreen
        {...MESSAGE_SCREENS.ALREADY_REGISTERED}
        backLink={getSignInUrl(getOwnProfileUrl())}
      />
    )

  if (alreadyItfCustomer) return <WelcomeExistingItfCustomer regToken={registrationToken} />

  if (registrationToken) return <StartRegistrationFlow token={registrationToken} />

  if (displayError) return <ErrorScreen {...displayError} />

  if (isAddingPerson || isRequestingToken || !isRecaptchaReady) return <LoadingSpinner />

  return (
    <>
      <h1 className={styles.header}>{t('header', { ns: 'strRegister' })}</h1>

      <p className={styles.subheader}>{t('subheader', { ns: 'strRegister' })}</p>

      <p className={styles.mandatoryInfo}>{t('form.allFieldsMandatory', { ns: 'strCommonUI' })}</p>

      <Formik
        enableReinitialize
        initialValues={initialFormValues}
        validate={handleValidate}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async ({
          nativeGivenName,
          nativeFamilyName,
          birthDate,
          sex,
          nationalityCode,
          countryOfResidenceCode,
          emailAddress,
          commConsentItf,
          commConsentNa,
          parentGuardianFullName,
          parentGuardianEmail,
          persona,
          personaDetail,
          whereDidYouHearAboutUs,
          whereDidYouHearDetail,
          whereDidYouHearNa,
        }) => {
          const parentalConsentRequired = !!birthDate && !isOfAge(birthDate)

          const person: WalkUpPersonDetailsInput = {
            nativeGivenName,
            nativeFamilyName,
            birthDate,
            sex: sex as SexEnum,
            nationalityCode,
            countryOfResidenceCode: convertResidenceLongFormToAlpha3(countryOfResidenceCode),
            emailAddress,
            signUpType: SignUpTypeEnum.WALKUP,
          }

          if (parentalConsentRequired) {
            person['parentFullName'] = parentGuardianFullName
            person['parentEmailAddress'] = parentGuardianEmail
          }

          if (persona) person['personaType'] = persona
          if (personaDetail && persona === Persona.OTHER) person['personaDetail'] = personaDetail

          if (whereDidYouHearAboutUs) person['whereDidYouHearAboutUs'] = whereDidYouHearAboutUs
          if (whereDidYouHearAboutUs === WhereDidYouHearOptionEnum.OTHER && whereDidYouHearDetail)
            person['whereDidYouHearDetail'] = whereDidYouHearDetail
          if (whereDidYouHearAboutUs === WhereDidYouHearOptionEnum.NA && whereDidYouHearNa)
            person['whereDidYouHearNa'] = whereDidYouHearNa

          Storage.saveRegistrationData({
            commConsentItf,
            commConsentNa,
            termsAndConditions: new Date().toISOString(),
            isWalkUpUser: true,
          })

          const recaptchaToken = await getRecaptchaToken()
          if (recaptchaToken) {
            dispatch(addWalkUpPerson(person, { action: 'walkUpReg', token: recaptchaToken }))
          }
        }}
      >
        {({ errors, values, setFieldValue, setFieldTouched, setFieldError }) => {
          const parentalConsentRequired = !!values.birthDate && !isOfAge(values.birthDate)
          const showFullForm =
            values.countryOfResidenceCode && isResidenceValid && !isValidatingResidence

          return (
            <Form>
              <div>
                <FormInputGroup
                  label={labels.countryOfResidence}
                  formikName={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
                  classWrapper={styles.inputGroup}
                >
                  <SearchFieldWTN
                    disabled={isValidatingResidence}
                    isLoading={isValidatingResidence}
                    options={residenceOptions}
                    name={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
                    placeholderText={PLACEHOLDERS.SEARCH_FIELD.COUNTRY_OF_RESIDENCE}
                    onChangeCallback={async value => {
                      const countryCode = convertResidenceLongFormToAlpha3(value)
                      setFieldValue(FormikNames.COUNTRY_OF_RESIDENCE_CODE, countryCode)
                      setFieldTouched(FormikNames.COUNTRY_OF_RESIDENCE_CODE, false)
                      setFieldError(FormikNames.COUNTRY_OF_RESIDENCE_CODE, undefined)
                      setIsResidenceValid(false)
                      setIsValidatingResidence(true)

                      let errorMessage

                      if (countryCode) {
                        try {
                          errorMessage =
                            (await registrationApi.getForbiddenRegistrationMessage(countryCode)) ??
                            undefined

                          if (!errorMessage) {
                            setIsResidenceValid(true)
                          }
                        } catch (err) {
                          errorMessage = t('residenceValidationError', { ns: 'strRegister' })
                        }
                      }

                      setFieldError(FormikNames.COUNTRY_OF_RESIDENCE_CODE, errorMessage)
                      setFieldTouched(FormikNames.COUNTRY_OF_RESIDENCE_CODE, !!errorMessage)
                      setIsValidatingResidence(false)
                    }}
                    renderErrorMessage={msg => <div dangerouslySetInnerHTML={{ __html: msg }} />}
                  />
                </FormInputGroup>

                {showFullForm && (
                  <>
                    <FormInputGroup
                      label={labels.givenName}
                      formikName={FormikNames.NATIVE_GIVEN_NAME}
                      classWrapper={styles.inputGroup}
                    >
                      <InputWTN
                        id={FormikNames.NATIVE_GIVEN_NAME}
                        name={FormikNames.NATIVE_GIVEN_NAME}
                        className={cx(styles.commonTextInput, styles.input)}
                      />
                    </FormInputGroup>

                    <FormInputGroup
                      label={labels.familyName}
                      formikName={FormikNames.NATIVE_FAMILY_NAME}
                      classWrapper={styles.inputGroup}
                    >
                      <InputWTN
                        id={FormikNames.NATIVE_FAMILY_NAME}
                        name={FormikNames.NATIVE_FAMILY_NAME}
                        className={cx(styles.commonTextInput, styles.input)}
                      />
                    </FormInputGroup>

                    <FormInputGroup
                      label={labels.dateOfBirth}
                      formikName={FormikNames.BIRTH_DATE}
                      classWrapper={styles.inputGroup}
                    >
                      <DateSelectGroup
                        valueKeys={{
                          fullDate: FormikNames.BIRTH_DATE,
                          day: FormikNames.DOB_DAY,
                          month: FormikNames.DOB_MONTH,
                          year: FormikNames.DOB_YEAR,
                        }}
                      />
                    </FormInputGroup>

                    {parentalConsentRequired && <ParentalConsent withContainer />}

                    <FormInputGroup
                      label={
                        <p
                          id={FormikNames.SEX}
                          data-formik-error={FormikNames.SEX}
                          className={styles.formLabel}
                        >
                          {labels.sex}
                        </p>
                      }
                      formikName={FormikNames.SEX}
                      classWrapper={styles.inputGroup}
                    >
                      <SexInputGroup formikName={FormikNames.SEX} />
                    </FormInputGroup>

                    <FormInputGroup
                      label={labels.nationality}
                      formikName={FormikNames.NATIONALITY_CODE}
                      classWrapper={styles.inputGroup}
                    >
                      <SearchFieldWTN
                        options={nationalityOptions}
                        name={FormikNames.NATIONALITY_CODE}
                        placeholderText={PLACEHOLDERS.SEARCH_FIELD.NATIONALITY}
                      />
                    </FormInputGroup>

                    <FormInputGroup
                      label={labels.email}
                      formikName={FormikNames.EMAIL_ADDRESS}
                      classWrapper={styles.inputGroup}
                    >
                      <InputWTN
                        id={FormikNames.EMAIL_ADDRESS}
                        name={FormikNames.EMAIL_ADDRESS}
                        className={cx(styles.commonTextInput, styles.input)}
                      />
                    </FormInputGroup>

                    <PersonaSelection
                      persona={values[FormikNames.PERSONA]}
                      personaDetail={values[FormikNames.PERSONA_DETAIL]}
                      hasErrorPersonaDetail={!!errors[FormikNames.PERSONA_DETAIL]}
                    />

                    <WhereDidYouHearSelection
                      whereDidYouHearAboutUs={values[FormikNames.WHERE_DID_YOU_HEAR_ABOUT_US]}
                      whereDidYouHearDetail={values[FormikNames.WHERE_DID_YOU_HEAR_DETAIL]}
                      whereDidYouHearNa={values[FormikNames.WHERE_DID_YOU_HEAR_NA]}
                      hasErrorWhereDidYouHearDetail={
                        !!errors[FormikNames.WHERE_DID_YOU_HEAR_DETAIL]
                      }
                      nationalAssociationOptions={formatNationalAssociationOptions(
                        nationalAssociationOptions
                      )}
                      loadingNationalAssociations={loadingProviders}
                    />

                    <hr></hr>
                    <div className={styles.inputGroup}>
                      <div className={styles.consents}>
                        <FormCheckbox
                          name={FormikNames.COMM_CONSENT_ITF}
                          label={t('commConsentItf', { ns: 'strRegister' })}
                        />
                        <FormCheckbox
                          name={FormikNames.COMM_CONSENT_NA}
                          label={t('commConsentNa', { ns: 'strRegister' })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {showFullForm && (
                <>
                  {' '}
                  <TermsAndPolicy
                    type={
                      parentalConsentRequired
                        ? TermsAndPolicyType.WELCOME_YOU_AND_PARENT
                        : TermsAndPolicyType.WELCOME
                    }
                    className={styles.termsText}
                  />
                  <BtnPrimary
                    size={'medium'}
                    type='submit'
                    className={styles.submitBtn}
                    isDisabled={!isRecaptchaReady}
                  >
                    {t('button.continue', { ns: 'strCommonUI' })}
                  </BtnPrimary>
                </>
              )}
              <ScrollToFormError />
            </Form>
          )
        }}
      </Formik>
      <DisclaimerRecaptcha className={styles.disclaimer} />
    </>
  )
}

const WelcomeUserWithRecaptcha = withRecaptcha(WelcomeUser)

export default WelcomeUserWithRecaptcha
